import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ButtonComponent from '../../components/ButtonComponent';
import InputField from '../../components/form/InputField';
import useFormInput from '../../custom-hooks/useFormInput';
import AuthLayout from '../../layouts/AuthLayout';
import { LuFingerprint, LuLock, LuMail } from 'react-icons/lu';
import {
  MembersRequestResetDefault,
  MembersRequestResetSchema,
  MembersRequestResetType,
  MembersResetPasswordDefault,
  MembersResetPasswordSchema,
  MembersResetPasswordType,
} from '../../app-schemas/authentication.schema';
import { SITELINK } from '../../app-routes/Links';
import { z } from 'zod';
import auth from '../../api-services/auth/authentication.services';
import { FEED_MSSG, RES_MSSG } from '../../api-services/ConstMssg';
// import OTPInput from 'react-otp-input';
import { ForgotPasswordImg, EmailCampaign } from '../../assets/images';

const RequestResetPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [otp, setOtp] = useState('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState(150);
  const [resendCode, setResendCode] = useState<boolean>(true);
  const [buttonStyle, setButtonStyle] = useState<string>('fixed');

  // handle otp input
  const handleOTPChange = (otp: any) => {
    setOtp(otp);
  };

  // custom hook to get && validate users input page 1
  const [formData, handleChange, errors, setValidationErrors] = useFormInput(
    MembersRequestResetDefault,
    MembersRequestResetSchema,
  );

  const [formDataPassword, handleChangePassword, errorsPassword, setValidationErrorsPassword] =
    useFormInput(MembersResetPasswordDefault, MembersResetPasswordSchema);

  useEffect(() => {
    if (otp.length === 6) {
      verify2FACode(otp, formData.email, handleVerifySuccess, handleError);
    }
  }, [otp, formData.email]);

  useEffect(() => {
    if (timeLeft === 0) {
      setResendCode(false);
    }
    if (timeLeft > 0) {
      const intervalId = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);

      // Clear the interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [timeLeft]);

  // Format time into MM:SS
  const formatTime = (seconds: any) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const inputFocus = () => {
    setButtonStyle('relative');
  };

  const inputBlur = () => {
    setButtonStyle('fixed');
  };

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    const { success, data, error } = MembersRequestResetSchema.safeParse(formData);

    if (success) {
      await RequestReset(data);
    } else {
      handleValidationErrors(error);
    }
  };

  const RequestReset = async (data: MembersRequestResetType) => {
    setLoading(true);
    try {
      const res = await auth.RequestReset(data);
      if (res?.data?.message === RES_MSSG.SUCCESS) {
        handleSuccess(res.data);
      } else {
        handleError(res?.response?.data?.data?.email ?? 'Error occured, try again');
      }
    } catch (error) {
      setErrorMsg('An error occured!');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (res: any) => {
    setSuccessMsg(FEED_MSSG.CODE_SENT + ' ' + formData.email);
    setPage(2);
    setTimeLeft(150);
  };

  const handleError = (message: string) => {
    setErrorMsg(message);
  };

  const handleValidationErrors = (error: z.ZodError<MembersRequestResetType>) => {
    const newErrors: Partial<Record<keyof MembersRequestResetType, string>> = {};
    error.errors.forEach((err) => {
      newErrors[err.path[0] as keyof MembersRequestResetType] = err.message;
    });

    setValidationErrors(newErrors);
  };

  // -- [2] VERIFY EMAIL
  const handleVerify2FA = async (e: FormEvent) => {
    e.preventDefault();

    setErrorMsg('');
    setSuccessMsg('');

    if (otp.length === 6) {
      await verify2FACode(otp, formData.email, handleVerifySuccess, handleError);
    } else {
      setSuccessMsg(FEED_MSSG.OTP_LENGTH_SHORT);
    }
  };

  const verify2FACode = async (
    otp: string,
    email: string,
    handleVerifySuccess: any,
    handleError: any,
  ) => {
    setLoading(true);

    const data = {
      verification_code: otp,
      email: email,
    };

    try {
      const res = await auth.VerifyCode(data);

      if (res?.data?.message === RES_MSSG.VERIFIED) {
        handleVerifySuccess(res.data);
      } else {
        handleError(res?.response?.data?.message);
      }
    } catch (error) {
      setErrorMsg('An error occured!');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySuccess = (res: any) => {
    setSuccessMsg(FEED_MSSG.VERIFIED);
    setPage(3);
  };

  // -- [3] UPDATE PASSWORD
  const handlePasswordUpdate = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    const { success, data, error } = MembersResetPasswordSchema.safeParse(formDataPassword);

    if (success) {
      await requestUpdate(data);
    } else {
      handleUpdateValidationErrors(error);
    }
  };

  const requestUpdate = async (data: MembersResetPasswordType) => {
    setLoading(true);
    const formdata = {
      email: formData.email,
      code: otp,
      new_password: data.new_password,
    };

    try {
      const res = await auth.UpdatePassword(formdata);

      if (res?.data?.message === RES_MSSG.SUCCESS) {
        handleUpdateSuccess(res.data);
      } else {
        handleError(res?.response?.data?.message);
      }
    } catch (error) {
      setErrorMsg('An error occured!');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSuccess = (res: any) => {
    setSuccessMsg(FEED_MSSG.RESET_OK);

    navigate(SITELINK.LOGIN);
  };
  
  const handleUpdateValidationErrors = (error: z.ZodError<MembersResetPasswordType>) => {
    const newErrors: Partial<Record<keyof MembersResetPasswordType, string>> = {};
    error.errors.forEach((err) => {
      newErrors[err.path[0] as keyof MembersResetPasswordType] = err.message;
    });

    setValidationErrorsPassword(newErrors);
  };

  return (
    <>
      {/* Goback links */}
      <div className="bg-white p-6 pb-0">
        <Link
          to={'/'}
          className="flex gap-1 text-start text-sm font-medium font-medium text-custom-600"
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.5625 14.0625H25.3125C25.5611 14.0625 25.7996 14.1613 25.9754 14.3371C26.1512 14.5129 26.25 14.7514 26.25 15C26.25 15.2486 26.1512 15.4871 25.9754 15.6629C25.7996 15.8387 25.5611 15.9375 25.3125 15.9375H6.5625C6.31386 15.9375 6.0754 15.8387 5.89959 15.6629C5.72377 15.4871 5.625 15.2486 5.625 15C5.625 14.7514 5.72377 14.5129 5.89959 14.3371C6.0754 14.1613 6.31386 14.0625 6.5625 14.0625Z"
              fill="black"
            />
            <path
              d="M6.95084 15L14.7265 22.7738C14.9025 22.9498 15.0014 23.1885 15.0014 23.4375C15.0014 23.6865 14.9025 23.9252 14.7265 24.1013C14.5504 24.2773 14.3117 24.3762 14.0627 24.3762C13.8138 24.3762 13.575 24.2773 13.399 24.1013L4.96146 15.6638C4.87415 15.5767 4.80489 15.4732 4.75763 15.3593C4.71036 15.2454 4.68604 15.1233 4.68604 15C4.68604 14.8767 4.71036 14.7546 4.75763 14.6407C4.80489 14.5268 4.87415 14.4233 4.96146 14.3363L13.399 5.89875C13.575 5.72271 13.8138 5.62382 14.0627 5.62382C14.3117 5.62382 14.5504 5.72271 14.7265 5.89875C14.9025 6.07479 15.0014 6.31355 15.0014 6.5625C15.0014 6.81146 14.9025 7.05021 14.7265 7.22625L6.95084 15Z"
              fill="black"
            />
          </svg>
        </Link>
      </div>

      <AuthLayout title="Forgot password">
        {/* enter email section */}
        {page === 1 && (
          <section>
            {/* hero-text area */}
            <div className="flex flex-col items-start mt-2">
              <h4 className="text-[32px] font-[400] text-black madimi-one flex gap-1 items-center">
                Password Reset
              </h4>

              <p className="text-[12px] font-[700] text-black flex gap-1 items-center pr-24">
                Enter your email address to reset password
              </p>
            </div>

            {/* hero image */}
            <div className="flex flex-col items-center mt-2">
              <img src={ForgotPasswordImg} alt="ForgotPasswordImg" />
            </div>

            {/* complete form area */}
            <form onSubmit={handlePasswordReset} className="grid grid-cols-2 gap-4 mt-2">
              <p className="font-[400] madimi-one text-black text-[20px]">Step 1 of 3</p>
              {/* enter email */}
              <InputField
                className="col-span-full text-sm rounded-lg font-semibold"
                type="email"
                id="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                onFocus={inputFocus}
                onBlur={inputBlur}
                required
                icon={LuMail}
                error={errors?.email}
              />
              {errorMsg && (
                <div className="text-red-600 text-[10px] col-span-full text-center">{errorMsg}</div>
              )}
              {successMsg && (
                <div className="text-green-600 text-[10px] col-span-full text-center">
                  {successMsg}
                </div>
              )}
              {/* action btn */}

              <div
                className={`${buttonStyle} right-0 left-0 bottom-0 bg-white w-full col-span-full`}
              >
                <div className={`${buttonStyle === 'fixed' && 'px-6'} w-full`}>
                  <ButtonComponent
                    type="submit"
                    className="mb-5 text-[14px] font-[700] py-3 rounded-md text-white bg-primary-blue-200"
                    disabled={loading}
                    loading={loading}
                  >
                    Continue
                  </ButtonComponent>
                </div>
              </div>
            </form>
          </section>
        )}

        {/* enter reset code section */}
        {page === 2 && (
          <section>
            {/* hero-text area */}
            <div className="flex flex-col items-start mt-2">
              <h4 className="font-[400] madimi-one text-black text-[32px]">Enter Reset Code</h4>
              <p className="text-[12px] font-[700] text-black flex gap-1 items-center">
                Enter the code sent to your email address
                <br /> {formData.email}
              </p>
            </div>

            {/* hero image */}
            <div className="flex flex-col items-center mt-2">
              <img src={EmailCampaign} alt="EmailCampaign" />
            </div>

            {/* complete form area */}
            <form onSubmit={handleVerify2FA} className="mt-2 w-full">
              <p className="font-[400] madimi-one text-black text-[20px]">Step 2 of 3</p>

              <div className="flex w-full justify-start items-start mb-4 mt-2 hidden">
                {/* <OTPInput
                  value={otp}
                  onChange={handleOTPChange}
                  // onFocus={inputFocus}
                  // onBlur={inputBlur}
                  numInputs={6}
                  shouldAutoFocus
                  inputStyle={{
                    width: '16%',
                    height: '50px',
                    margin: '0 0.5rem 0 0rem',
                    fontSize: '1.2rem',
                    borderRadius: 5,
                    border: '1px solid #6d7986',
                  }}
                  renderInput={(props) => <input {...props} />}
                /> */}
              </div>

              <div className="col-span-full">
                <InputField
                  className="col-span-full text-[12px] rounded-md font-medium"
                  type="text"
                  id="otp"
                  placeholder="OTP"
                  value={otp}
                  onChange={(e) => handleOTPChange(e.target.value)}
                  onFocus={inputFocus}
                  onBlur={inputBlur}
                  icon={LuLock}
                  maxLength={6}
                  // error={errors?.password}
                />
              </div>

              <div className="text-red-600 text-[10px] col-span-full text-center mt-1">
                {errorMsg}
              </div>
              <div className="text-green-600 text-[10px] col-span-full text-center">
                {successMsg}
              </div>

              {/* action btn */}
              <div
                className={`${buttonStyle} right-0 left-0 bottom-0 bg-white w-full col-span-full mt-4`}
              >
                <div className={`${buttonStyle === 'fixed' && 'px-6'} w-full`}>
                  <ButtonComponent
                    type="submit"
                    className="text-[14px] font-[700] py-3 rounded-md text-white bg-primary-blue-200 mb-1"
                    disabled={loading}
                    loading={loading}
                  >
                    Verify Code
                  </ButtonComponent>

                  {/* extra reset links */}
                  <div className="text-center">
                    {timeLeft > 0 && (
                      <p className="text-gray-600 text-sm font-medium text-center mb-0">
                        Resend code in {formatTime(timeLeft)}
                      </p>
                    )}

                    <div className="text-center text-sm font-medium text-gray-600 flex justify-center ">
                      <span>Did not get the code? </span>
                      <button
                        type="button"
                        disabled={resendCode}
                        onClick={() => RequestReset({ email: formData.email })}
                        className="disabled:text-gray-400 disabled:cursor-not-allowed text-blue-500 text-semibold h-[0px] flex flex-col"
                      >
                        <u>Resend code</u>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </section>
        )}

        {/* enter preferred password section */}
        {page === 3 && (
          <section>
            {/* hero-text area */}
            <div className="flex flex-col items-start mt-2">
              <h4 className="font-[400] madimi-one text-black text-[32px]">Update your Password</h4>
              <p className="text-[12px] font-[700] text-black flex gap-1 items-center">
                Enter your preferred password
              </p>

              <p className="mt-12 font-[400] madimi-one text-[20px]">Step 3 of 3</p>
            </div>

            {/* complete form area */}
            <form onSubmit={handlePasswordUpdate} className="grid grid-cols-2 gap-4 mt-2">
              {/* enter password */}
              <InputField
                className="col-span-full text-sm rounded-lg"
                type="password"
                id="new_password"
                placeholder="Enter Password"
                value={formDataPassword.new_password}
                onChange={handleChangePassword}
                onFocus={inputFocus}
                onBlur={inputBlur}
                required
                icon={LuFingerprint}
                error={errorsPassword?.new_password}
              />

              {/* enter password */}
              <div className="col-span-full">
                <InputField
                  className="col-span-full  text-sm rounded-lg"
                  type="password"
                  id="confirm_password"
                  placeholder="Re-enter Password"
                  value={formDataPassword.confirm_password}
                  onChange={handleChangePassword}
                  onFocus={inputFocus}
                  onBlur={inputBlur}
                  required
                  icon={LuFingerprint}
                  error={errorsPassword?.confirm_password}
                />
              </div>

              {errorMsg && (
                <div className="text-red-600 text-[10px] col-span-full text-center">{errorMsg}</div>
              )}
              {successMsg && (
                <div className="text-green-600 text-[10px] col-span-full text-center">
                  {successMsg}
                </div>
              )}

              <div
                className={`${buttonStyle} right-0 left-0 bottom-0 bg-white w-full col-span-full`}
              >
                <div className={`${buttonStyle === 'fixed' && 'px-6'} w-full`}>
                  {/* action btn */}
                  <ButtonComponent
                    type="submit"
                    className="mb-5 text-[14px] font-[700] py-3 rounded-md text-white bg-primary-blue-200"
                    disabled={loading}
                    loading={loading}
                  >
                    Update Password
                  </ButtonComponent>
                </div>
              </div>
            </form>
          </section>
        )}
      </AuthLayout>
    </>
  );
};

export default RequestResetPassword;
